import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Mic, MicOff, Play, RotateCcw } from 'lucide-react';
import { cn } from 'lib/utils';
import { useIsMobile } from 'hooks/use-media-query';
import * as Api from '../../api';
import { canFollowUp, formatClock, isSpokenAnswer, speakingPrompts, withFollowUps, type SpeakingPrompt } from '../../constants';
import { useSpeakingRecorder } from '../../hooks/useSpeakingRecorder';
import { askAloud, unlockAudio } from './examinerVoice';
import { VoiceOrb } from './VoiceOrb';
import type { OrbState } from './voiceOrbMotion';
import type { CandidateSection, SpeakingAnswer, SpeakingRecording } from '../../types';

/** The answer starts by itself this long after the question, as an examiner would wait. */
const AUTO_START_SECONDS = 5;
const UPLOAD_RETRY_DELAYS_MS = [1000, 3000];
/** Longer than the server ever takes (about 32s worst case), so a question is never stored after we stopped waiting for it. */
const FOLLOW_UP_TIMEOUT_MS = 40_000;
const FOLLOW_UP_KEY_SUFFIX = /-f1$/;
const RING_RADIUS = 42;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;
const ORB_SIZE = 240;
const ORB_SIZE_MOBILE = 150;

type Phase = 'intro' | 'asking' | 'prep' | 'ready' | 'recording' | 'saving' | 'failed' | 'done';

interface Props {
  attemptId: string;
  section: CandidateSection;
  answers: Map<string, unknown>;
  /** Mirrors a stored recording into the session so progress and counts stay right. */
  onRecorded: (questionId: string, value: SpeakingAnswer) => void;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * IELTS Speaking: the examiner's question is read aloud (a recorded examiner
 * voice, else the browser's), the candidate answers
 * into the microphone and each answer uploads as soon as it ends. Nothing can
 * be re-recorded, and a reload resumes at the first unanswered question.
 */
export function SpeakingSectionView({ attemptId, section, answers, onRecorded }: Props) {
  const { t } = useTranslation();
  const question = section.questions.find((q) => q.type === 'SPEAKING');
  const scriptedPrompts = useMemo(() => speakingPrompts(question?.speakingParts), [question?.speakingParts]);
  const stored = (question ? answers.get(question.id) : undefined) as SpeakingAnswer | undefined;
  const recorded = useMemo(() => stored ?? {}, [stored]);
  // The live examiner's follow-ups join the scripted list as they are asked, so a reload resumes at one.
  const prompts = useMemo(() => withFollowUps(scriptedPrompts, recorded), [scriptedPrompts, recorded]);
  const index = prompts.findIndex((p) => !isSpokenAnswer(recorded[p.key]));
  const current: SpeakingPrompt | undefined = index >= 0 ? prompts[index] : undefined;

  const currentKeyRef = useRef(current?.key);
  currentKeyRef.current = current?.key;
  // The clock can close the section mid-answer; the API allows a short grace, so still send it.
  const recorder = useSpeakingRecorder((audio) => {
    const key = currentKeyRef.current;
    if (!question || !key) return;
    Api.UploadSpeaking(attemptId, section.id, question.id, key, audio.blob, audio.durationSec).catch(() => undefined);
  });
  const { requestMic, start: startRecording, stop: stopRecording } = recorder;
  const [phase, setPhase] = useState<Phase>(index < 0 ? 'done' : 'intro');
  const [repeated, setRepeated] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [notes, setNotes] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [examinerListening, setExaminerListening] = useState(false);
  const pendingRef = useRef<{ key: string; blob: Blob; durationSec: number } | null>(null);
  const cancelSpeechRef = useRef<() => void>(() => undefined);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const examinerUrls = useRef(new Map<string, Promise<string | null>>());

  const isMobile = useIsMobile();

  useEffect(() => () => cancelSpeechRef.current(), []);

  // One request per prompt; a failed one is forgotten so the next ask retries.
  const examinerUrl = useCallback(
    (key: string) => {
      const cached = examinerUrls.current.get(key);
      if (cached) return cached;
      // A follow-up arrives with its own recording, or is read in the browser's voice after a reload.
      if (!question || FOLLOW_UP_KEY_SUFFIX.test(key)) return Promise.resolve(null);
      const request = Api.ExaminerAudio(attemptId, section.id, question.id, key)
        .then(({ data }) => data.url ?? null)
        .catch(() => {
          examinerUrls.current.delete(key);
          return null;
        });
      examinerUrls.current.set(key, request);
      return request;
    },
    [attemptId, section.id, question],
  );

  // Fetch this question and the next ahead of time, so the examiner speaks without a pause.
  useEffect(() => {
    if (index >= 0) prompts.slice(index, index + 2).forEach((p) => examinerUrl(p.key));
  }, [index, prompts, examinerUrl]);

  const ask = useCallback((prompt: SpeakingPrompt) => {
    setPhase('asking');
    cancelSpeechRef.current();
    if (!audioRef.current) audioRef.current = new Audio();
    cancelSpeechRef.current = askAloud(audioRef.current, examinerUrl(prompt.key), prompt.text, () => {
      if (prompt.prepSeconds > 0) {
        setCountdown(prompt.prepSeconds);
        setPhase('prep');
      } else {
        setCountdown(AUTO_START_SECONDS);
        setPhase('ready');
      }
    });
  }, [examinerUrl]);

  const begin = async () => {
    if (!current) return;
    // Before the first await: the tap that starts the test is what lets the examiner be heard (see unlockAudio).
    if (!audioRef.current) audioRef.current = new Audio();
    unlockAudio(audioRef.current);
    if (await requestMic()) ask(current);
  };

  const startAnswer = useCallback(() => {
    cancelSpeechRef.current();
    if (startRecording()) setPhase('recording');
  }, [startRecording]);

  // Prep and auto-start countdowns tick once a second.
  useEffect(() => {
    if (phase !== 'prep' && phase !== 'ready') return undefined;
    if (countdown <= 0) {
      startAnswer();
      return undefined;
    }
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, countdown, startAnswer]);

  // The live examiner's follow-up to the answer just saved, or null: it is off, has nothing to ask, or is too slow.
  const askFollowUp = useCallback(
    async (promptKey: string): Promise<{ key: string; text: string } | null> => {
      const asked = prompts.find((p) => p.key === promptKey);
      if (!question || !asked || !canFollowUp(asked)) return null;
      setExaminerListening(true);
      try {
        const { data } = await Api.SpeakingFollowUp(attemptId, section.id, question.id, promptKey, FOLLOW_UP_TIMEOUT_MS);
        if (!data.key || !data.text) return null;
        examinerUrls.current.set(data.key, Promise.resolve(data.url ?? null));
        return { key: data.key, text: data.text };
      } catch {
        // Never costs the candidate the test: the scripted questions carry on.
        return null;
      } finally {
        setExaminerListening(false);
      }
    },
    [attemptId, section.id, question, prompts],
  );

  const upload = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending || !question) return;
    setPhase('saving');
    setUploadError('');
    // The stored answer, plus the follow-up the examiner asked about it (stored together so the next question is ready).
    const settle = async (entry: SpeakingRecording, canAskFollowUp: boolean) => {
      pendingRef.current = null;
      const followUp = canAskFollowUp ? await askFollowUp(pending.key) : null;
      onRecorded(question.id, {
        ...recorded,
        [pending.key]: { ...recorded[pending.key], ...entry },
        ...(followUp ? { [followUp.key]: { prompt: followUp.text, askedAt: new Date().toISOString() } } : {}),
      });
    };
    for (let attempt = 0; ; attempt += 1) {
      try {
        const { data } = await Api.UploadSpeaking(attemptId, section.id, question.id, pending.key, pending.blob, pending.durationSec);
        await settle(
          { audioKey: '', durationSec: data.durationSec ?? pending.durationSec, recordedAt: new Date().toISOString() },
          data.followUp === true,
        );
        break;
      } catch (e: any) {
        const code = e?.response?.data?.code;
        // Stored already, or a follow-up the server no longer has: either way there is nothing left to send.
        if (code === 'ALREADY_RECORDED' || code === 'FOLLOW_UP_NOT_ISSUED') {
          await settle({ audioKey: '' }, false);
          break;
        }
        if (attempt >= UPLOAD_RETRY_DELAYS_MS.length || code === 'SECTION_TIME_UP' || code === 'SECTION_LOCKED') {
          setUploadError(e?.response?.data?.message || t('exam.speaking.upload_failed'));
          setPhase('failed');
          return;
        }
        await wait(UPLOAD_RETRY_DELAYS_MS[attempt]);
      }
    }
  }, [attemptId, section.id, question, recorded, onRecorded, askFollowUp, t]);

  const finishAnswer = useCallback(async () => {
    if (!current) return;
    const audio = await stopRecording();
    if (!audio) return;
    pendingRef.current = { key: current.key, blob: audio.blob, durationSec: audio.durationSec };
    await upload();
  }, [current, stopRecording, upload]);

  // The answer stops itself when its time is up.
  useEffect(() => {
    if (phase === 'recording' && current && recorder.elapsed >= current.answerSeconds) finishAnswer();
  }, [phase, current, recorder.elapsed, finishAnswer]);

  // Once an answer is stored, the next question is asked straight away.
  const lastKeyRef = useRef(current?.key);
  useEffect(() => {
    if (current?.key === lastKeyRef.current) return;
    lastKeyRef.current = current?.key;
    setRepeated(false);
    if (!current) {
      setPhase('done');
      return;
    }
    if (current.cueCard) setNotes('');
    if (phase !== 'intro') ask(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.key]);

  const repeat = () => {
    if (!current || repeated) return;
    setRepeated(true);
    ask(current);
  };

  if (!question || !prompts.length) {
    return <p className="p-8 text-center text-gray-600">{t('exam.empty_section')}</p>;
  }

  const orbState: OrbState =
    phase === 'asking' ? 'examiner' : phase === 'recording' ? 'candidate' : phase === 'saving' && examinerListening ? 'thinking' : 'idle';
  // Numbered by the scripted questions: a follow-up is not one of them.
  const partPrompts = current ? scriptedPrompts.filter((p) => p.part === current.part) : [];
  const numberInPart = current ? partPrompts.findIndex((p) => p.key === current.key) + 1 : 0;
  const recordedCount = prompts.filter((p) => isSpokenAnswer(recorded[p.key])).length;
  const candidateTurn = phase === 'recording';
  const asking = phase !== 'intro' && phase !== 'done' ? current : undefined;
  const preparing = phase === 'prep' && Boolean(current?.cueCard);
  const orbSize = isMobile ? ORB_SIZE_MOBILE : ORB_SIZE;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <StageHeader prompts={scriptedPrompts} recorded={recorded} part={asking?.part} savedCount={recordedCount} />

      <main className="flex min-h-0 min-w-0 flex-1 flex-col items-center gap-4 overflow-y-auto overflow-x-hidden px-4 py-4 text-center sm:gap-6 sm:px-10 sm:py-5 [&>:first-child]:mt-auto [&>:last-child]:mb-auto">
        {phase === 'intro' ? (
          <MicCheck
            status={recorder.micStatus}
            level={recorder.level}
            resuming={recordedCount > 0}
            onStart={begin}
            onRetry={() => void requestMic()}
          />
        ) : phase === 'done' ? (
          <DoneCard parts={new Set(scriptedPrompts.map((p) => p.part)).size} answers={recordedCount} />
        ) : current && preparing ? (
          <CueCard
            text={current.text}
            notes={notes}
            onNotes={setNotes}
            orb={<VoiceOrb state="idle" size={isMobile ? 56 : 110} />}
            heading={t('exam.speaking.prep_title')}
          />
        ) : current ? (
          <>
            <p className="font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#646D84] sm:text-[11.5px]">
              {current.followUp
                ? t('exam.speaking.follow_up_label', { part: current.part })
                : current.cueCard
                  ? t('exam.speaking.cue_label', { part: current.part })
                  : t('exam.speaking.examiner_label', { part: current.part, n: numberInPart, total: partPrompts.length })}
            </p>

            {/* Moves like speech while the examiner asks; follows the candidate's microphone while they speak. */}
            <div className={cn('shrink-0 transition-[filter,opacity] duration-500', phase === 'failed' && 'opacity-70 grayscale')}>
              <VoiceOrb state={orbState} level={candidateTurn ? recorder.level : undefined} size={orbSize} />
            </div>

            {current.cueCard ? (
              <CueCard text={current.text} notes={notes} onNotes={setNotes} />
            ) : (
              <p
                className={cn(
                  'max-w-3xl break-words font-display text-[23px] font-extrabold leading-[1.22] tracking-[-0.035em] text-[#0A1628] transition-opacity duration-500 sm:text-[34px]',
                  phase === 'saving' && 'opacity-55',
                )}
              >
                {current.text}
              </p>
            )}

            {phase === 'failed' ? (
              <div
                role="alert"
                className="flex max-w-[640px] items-start gap-3 rounded-2xl border border-[#F5B5B0] bg-[#FDECEC] px-[18px] py-3.5 text-left text-sm leading-relaxed text-[#7A1A12]"
              >
                <AlertTriangle className="mt-px h-[22px] w-[22px] shrink-0" aria-hidden />
                <span>
                  <b>{t('exam.speaking.failed_lead')}</b> {uploadError}
                </span>
              </div>
            ) : candidateTurn ? (
              <p className="text-[13.5px] text-[#4B5468] sm:text-sm">{t('exam.speaking.your_turn')}</p>
            ) : phase === 'saving' ? (
              <p className="text-[13.5px] text-[#4B5468] sm:text-sm">
                {t(examinerListening ? 'exam.speaking.examiner_thinking' : 'exam.speaking.saving_status')}
              </p>
            ) : (
              <span className="inline-flex items-center gap-2.5 rounded-full bg-[#F1F4FB] py-[7px] pl-3.5 pr-2 text-[13px] font-semibold text-[#364055]">
                {t('exam.speaking.played_once')}
                <button
                  type="button"
                  onClick={repeat}
                  disabled={repeated || phase !== 'ready'}
                  title={t('exam.speaking.repeat_help')}
                  className="inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-full bg-white px-3.5 text-[12.5px] font-bold text-[#0A1628] shadow-[inset_0_0_0_1px_#D5DAE8] transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#0A1628] disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                  {t('exam.speaking.repeat')}
                </button>
              </span>
            )}
          </>
        ) : null}
      </main>

      {asking && (
        <AnswerDock
          phase={phase}
          countdown={countdown}
          prepSeconds={asking.prepSeconds}
          elapsed={recorder.elapsed}
          answerSeconds={asking.answerSeconds}
          level={recorder.level}
          examinerListening={examinerListening}
          onStart={startAnswer}
          onFinish={finishAnswer}
          onRetry={upload}
        />
      )}
    </div>
  );
}

/** Title, the three parts (a row of bars on a phone) and how many answers are stored. */
function StageHeader({
  prompts,
  recorded,
  part,
  savedCount,
}: {
  prompts: SpeakingPrompt[];
  recorded: SpeakingAnswer;
  part?: number;
  savedCount: number;
}) {
  const { t } = useTranslation();
  const parts = [1, 2, 3]
    .map((n) => {
      const items = prompts.filter((p) => p.part === n);
      return { n, total: items.length, done: items.filter((item) => isSpokenAnswer(recorded[item.key])).length };
    })
    .filter((p) => p.total);
  const state = (p: { n: number; total: number; done: number }) => (p.done === p.total ? 'ok' : p.n === part ? 'on' : 'idle');

  return (
    <header className="shrink-0 border-b border-[#E6E9F2]">
      <div className="flex items-center gap-4 px-4 py-2.5 sm:gap-6 sm:px-7 sm:py-3.5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-[#D1FE17]">
            <Mic className="h-[18px] w-[18px] text-[#0A1628]" aria-hidden />
          </span>
          <div className="flex min-w-0 flex-col text-left leading-tight">
            <b className="truncate font-display text-[15px] font-extrabold text-[#0A1628]">{t('exam.speaking.type')}</b>
            {part ? <span className="truncate text-xs text-[#646D84]">{t(`exam.speaking.part${part}_short`)}</span> : null}
          </div>
        </div>

        <ol aria-label={t('exam.speaking.progress')} className="mx-auto hidden gap-7 sm:flex">
          {parts.map((p) => {
            const s = state(p);
            return (
              <li
                key={p.n}
                aria-current={s === 'on' ? 'step' : undefined}
                className={cn(
                  'flex items-center gap-2.5 text-[13.5px] font-bold',
                  s === 'ok' ? 'text-[#166534]' : s === 'on' ? 'text-[#0A1628]' : 'text-[#8A93A8]',
                )}
              >
                <i
                  className={cn(
                    'flex h-[26px] w-[26px] items-center justify-center rounded-full border-2 text-xs not-italic',
                    s === 'ok'
                      ? 'border-[#86C9A0] bg-[#E6F4EC]'
                      : s === 'on'
                        ? 'border-[#0A1628] bg-[#0A1628] text-white'
                        : 'border-[#CBD2E3]',
                  )}
                  aria-hidden
                >
                  {s === 'ok' ? '✓' : p.n}
                </i>
                {t('exam.speaking.part_n', { n: p.n })}
                <span className="sr-only">{` ${p.done}/${p.total}`}</span>
              </li>
            );
          })}
        </ol>

        <span className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-full bg-[#F1F4FB] px-3 py-1.5 text-xs font-semibold text-[#4B5468] sm:ml-0 sm:text-[12.5px]">
          <Check className="h-3.5 w-3.5 text-[#166534]" aria-hidden />
          {t('exam.speaking.saved_count', { count: savedCount })}
        </span>
      </div>

      <ol aria-label={t('exam.speaking.progress')} className="flex gap-1.5 px-4 pb-2.5 sm:hidden">
        {parts.map((p) => {
          const s = state(p);
          return (
            <li
              key={p.n}
              aria-current={s === 'on' ? 'step' : undefined}
              style={{ flexGrow: p.total }}
              className={cn(
                'flex-1 text-[11.5px] font-bold',
                s === 'ok' ? 'text-[#166534]' : s === 'on' ? 'text-[#0A1628]' : 'text-[#8A93A8]',
              )}
            >
              <i
                className={cn('mb-1.5 block h-1 rounded-full', s === 'ok' ? 'bg-[#86C9A0]' : s === 'on' ? 'bg-[#0A1628]' : 'bg-[#E6E9F2]')}
                aria-hidden
              />
              {t('exam.speaking.part_n', { n: p.n })}
              <span className="sr-only">{` ${p.done}/${p.total}`}</span>
            </li>
          );
        })}
      </ol>
    </header>
  );
}

const CARD = 'w-full max-w-[640px] rounded-[28px] border border-[#E6E9F2] bg-white px-6 py-8 shadow-[0_30px_60px_-40px_rgba(10,22,40,0.35)] sm:px-10 sm:py-9';
const PRIMARY_BUTTON =
  'inline-flex w-full items-center justify-center rounded-[14px] bg-[#0A1628] px-4 py-4 text-[15.5px] font-bold text-white transition hover:brightness-110 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-3 focus-visible:outline-[#0A1628] disabled:cursor-not-allowed disabled:opacity-40';

function MicCheck({
  status,
  level,
  resuming,
  onStart,
  onRetry,
}: {
  status: ReturnType<typeof useSpeakingRecorder>['micStatus'];
  level: number;
  resuming: boolean;
  onStart: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const blocked = status === 'denied' || status === 'unsupported';
  const live = status === 'ready';

  if (blocked) {
    return (
      <section className={cn(CARD, 'text-left')} aria-labelledby="speaking-mic-title">
        <span className="mx-auto flex h-[84px] w-[84px] items-center justify-center rounded-full bg-[#FDECEC] text-[#B42318]">
          <MicOff className="h-[38px] w-[38px]" aria-hidden />
        </span>
        <h2 id="speaking-mic-title" className="mt-[18px] text-center font-display text-[26px] font-extrabold tracking-[-0.04em] text-[#0A1628] sm:text-[28px]">
          {t(status === 'denied' ? 'exam.speaking.mic_denied_title' : 'exam.speaking.mic_unsupported_title')}
        </h2>
        <p role="alert" className="mt-[22px] rounded-2xl border border-[#F5B5B0] bg-[#FDECEC] px-[18px] py-4 text-sm leading-relaxed text-[#7A1A12]">
          {t(status === 'denied' ? 'exam.speaking.mic_denied' : 'exam.speaking.mic_unsupported')}
        </p>
        {status === 'denied' && (
          <ol className="mt-[18px]">
            {[1, 2, 3].map((n) => (
              <li key={n} className="flex gap-3 border-t border-[#EEF1F8] py-[11px] text-sm leading-normal text-[#364055] first:border-t-0">
                <i className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#0A1628] text-xs font-extrabold not-italic text-[#D1FE17]" aria-hidden>
                  {n}
                </i>
                {t(`exam.speaking.mic_step_${n}`)}
              </li>
            ))}
          </ol>
        )}
        <div className="mt-[22px] flex gap-3">
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex flex-1 items-center justify-center rounded-[14px] border border-[#D5DAE8] bg-white px-4 py-4 text-[15px] font-bold text-[#0A1628] transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#0A1628]"
          >
            {t('exam.speaking.mic_retry')}
          </button>
          <button type="button" disabled className={cn(PRIMARY_BUTTON, 'flex-1')}>
            {t(resuming ? 'exam.speaking.resume' : 'exam.speaking.begin')}
          </button>
        </div>
        <p className="mt-3.5 text-center text-[12.5px] text-[#646D84]">{t('exam.speaking.mic_denied_note')}</p>
      </section>
    );
  }

  return (
    <section className={cn(CARD, 'text-center')} aria-labelledby="speaking-intro-title">
      {/* Answers the question "does my microphone work?" before the test starts. */}
      <div className="mx-auto w-fit">
        <VoiceOrb state={live ? 'candidate' : 'idle'} level={live ? level : undefined} size={isMobile ? 110 : 150} />
      </div>
      <h2 id="speaking-intro-title" className="mt-5 font-display text-[26px] font-extrabold tracking-[-0.04em] text-[#0A1628] sm:text-[30px]">
        {t(resuming ? 'exam.speaking.resume_title' : 'exam.speaking.intro_title')}
      </h2>
      <p className="mx-auto mt-3 max-w-[46ch] text-[15px] leading-relaxed text-[#4B5468]">{t('exam.speaking.intro_help')}</p>
      {live && (
        <div role="status" className="mt-6 flex items-center justify-center gap-3.5 rounded-2xl bg-[#F1F4FB] px-[18px] py-3.5">
          <span className="text-sm font-bold text-[#166534]">{t('exam.speaking.mic_ok')}</span>
          <LevelBars level={level} />
        </div>
      )}
      {live && <p className="mt-3 text-[13px] text-[#646D84]">{t('exam.speaking.mic_say')}</p>}
      <button type="button" onClick={onStart} disabled={status === 'asking'} className={cn(PRIMARY_BUTTON, 'mt-[22px]')}>
        {t(resuming ? 'exam.speaking.resume' : 'exam.speaking.start')}
      </button>
    </section>
  );
}

function DoneCard({ parts, answers }: { parts: number; answers: number }) {
  const { t } = useTranslation();
  return (
    <section className="w-full max-w-[640px] text-center" aria-labelledby="speaking-done-title">
      <span className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-[#E6F4EC] text-[#166534] shadow-[0_0_0_12px_#F3FAF6]">
        <Check className="h-[46px] w-[46px]" strokeWidth={2.4} aria-hidden />
      </span>
      <h2 id="speaking-done-title" className="mt-7 font-display text-[28px] font-extrabold leading-[1.1] tracking-[-0.045em] text-[#0A1628] sm:text-4xl">
        {t('exam.speaking.all_done')}
      </h2>
      <p className="mx-auto mt-3 max-w-[46ch] text-[15.5px] leading-relaxed text-[#4B5468]">{t('exam.speaking.all_done_help')}</p>
      <dl className="mt-6 flex gap-3 text-left">
        {[
          { value: parts, label: t('exam.speaking.done_parts') },
          { value: answers, label: t('exam.speaking.done_answers') },
        ].map((stat) => (
          <div key={stat.label} className="flex-1 rounded-2xl border border-[#EEF1F8] bg-[#F7F9FD] px-4 py-3.5">
            <dd className="font-display text-2xl font-extrabold tracking-[-0.03em] text-[#0A1628]">{stat.value}</dd>
            <dt className="text-[12.5px] text-[#646D84]">{stat.label}</dt>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CueCard({
  text,
  notes,
  onNotes,
  orb,
  heading,
}: {
  text: string;
  notes: string;
  onNotes: (v: string) => void;
  /** Shown beside the notes while preparing. */
  orb?: React.ReactNode;
  heading?: string;
}) {
  const { t } = useTranslation();
  const [topic, ...points] = text.split('\n').map((l) => l.trim()).filter(Boolean);
  return (
    <div className="grid w-full min-w-0 max-w-[1100px] grid-cols-1 gap-4 text-left sm:gap-8 md:grid-cols-2 md:items-start">
      <section
        aria-labelledby="speaking-cue-topic"
        className="min-w-0 break-words rounded-3xl border border-[#F1DFA6] bg-[#FFF8E5] p-5 shadow-[0_24px_44px_-32px_rgba(146,64,14,0.5)] sm:px-8 sm:py-[30px]"
      >
        <p className="font-mono text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#92400E]">{t('exam.speaking.cue_card')}</p>
        <p id="speaking-cue-topic" className="mt-3 font-display text-xl font-extrabold leading-tight tracking-[-0.03em] text-[#0A1628] sm:text-[25px]">
          {topic}
        </p>
        {points.length > 0 && (
          <ul className="mt-3.5 list-disc space-y-0.5 pl-5 text-[15px] leading-relaxed text-[#4A3B12] sm:text-base sm:leading-[1.85]">
            {points.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
      </section>
      <div className="flex min-w-0 flex-col">
        {orb && (
          <div className="mb-3 flex items-center gap-4 sm:mb-4 sm:gap-[18px]">
            <div className="shrink-0">{orb}</div>
            {heading && <b className="font-display text-base font-extrabold tracking-[-0.02em] text-[#0A1628] sm:text-[19px]">{heading}</b>}
          </div>
        )}
        <label className="flex flex-col gap-2 text-sm">
          <span className="font-bold text-[#364055]">{t('exam.speaking.notes')}</span>
          <textarea
            value={notes}
            onChange={(e) => onNotes(e.target.value)}
            className="min-h-[96px] resize-none rounded-[20px] border-[1.5px] border-[#D5DAE8] bg-white p-3 text-base leading-relaxed text-[#0A1628] focus:outline-none focus-visible:border-[#0A1628] focus-visible:shadow-[0_0_0_3px_#EEF1F8] sm:min-h-[200px] sm:p-5 sm:text-[15.5px] sm:leading-[1.7]"
            placeholder={t('exam.speaking.notes_placeholder')}
          />
        </label>
      </div>
    </div>
  );
}

interface DockProps {
  phase: Phase;
  countdown: number;
  prepSeconds: number;
  elapsed: number;
  answerSeconds: number;
  level: number;
  examinerListening: boolean;
  onStart: () => void;
  onFinish: () => void;
  onRetry: () => void;
}

/** The one control strip: what is happening, the record button with its time ring, and what a tap does now. */
function AnswerDock({ phase, countdown, prepSeconds, elapsed, answerSeconds, level, examinerListening, onStart, onFinish, onRetry }: DockProps) {
  const { t } = useTranslation();
  const progress =
    phase === 'recording'
      ? Math.min(1, elapsed / answerSeconds)
      : phase === 'prep'
        ? 1 - countdown / Math.max(1, prepSeconds)
        : phase === 'ready'
          ? 1 - countdown / AUTO_START_SECONDS
          : 0;
  const red = phase === 'recording' || phase === 'failed';
  const stroke = red ? '#EF4444' : phase === 'saving' ? '#16A34A' : '#0A1628';

  const face =
    phase === 'recording'
      ? { label: t('exam.speaking.tap_to_finish'), aria: t('exam.speaking.finish_aria'), onClick: onFinish }
      : phase === 'failed'
        ? { label: t('exam.speaking.failed_hint'), aria: t('exam.speaking.retry'), onClick: onRetry }
        : phase === 'ready' || phase === 'prep'
          ? {
              label: t(phase === 'prep' ? 'exam.speaking.start_talking' : 'exam.speaking.tap_to_answer'),
              aria: t('exam.speaking.start_aria'),
              onClick: onStart,
            }
          : null;
  const hint = face?.label ?? (phase === 'asking' ? t('exam.speaking.dock_asking_hint') : t('exam.speaking.saving_hint'));
  const timer =
    phase === 'recording' ? (
      <span className="font-mono text-xl font-bold tabular-nums text-[#0A1628] sm:min-w-[120px] sm:text-right sm:text-[22px]">
        {formatClock(Math.floor(elapsed))} <small className="text-[13px] text-[#8A93A8] sm:text-sm">/ {formatClock(answerSeconds)}</small>
      </span>
    ) : phase === 'prep' ? (
      <span role="timer" aria-label={formatClock(countdown)} className="font-mono text-2xl font-bold tabular-nums text-[#0A1628] sm:text-[26px]">
        {formatClock(countdown)}
      </span>
    ) : null;

  return (
    <footer className="shrink-0 border-t border-[#E6E9F2] bg-white px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3.5 shadow-[0_-12px_30px_-22px_rgba(10,22,40,0.5)] sm:px-9 sm:py-4 sm:shadow-none">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-7 gap-y-3 sm:flex-nowrap">
        <div className="min-w-0 flex-1 basis-full text-left sm:basis-0" aria-live="polite">
          <StatusLine phase={phase} countdown={countdown} examinerListening={examinerListening} />
          <div className="mt-[3px] text-[12.5px] leading-snug text-[#646D84] sm:text-[13px]">{hint}</div>
        </div>

        {phase === 'recording' && (
          <div className="flex min-w-0 flex-1 justify-center sm:flex-none">
            <LevelBars level={level} />
          </div>
        )}
        {timer}

        <div className="relative h-[72px] w-[72px] shrink-0 sm:h-[84px] sm:w-[84px]">
          <svg viewBox="0 0 92 92" className="absolute inset-0 h-full w-full" aria-hidden>
            <circle cx="46" cy="46" r={RING_RADIUS} fill="none" stroke={red ? '#FDE2E2' : phase === 'saving' ? '#CDEBD6' : '#E6E9F2'} strokeWidth="4" />
            {phase === 'saving' ? (
              <circle
                cx="46"
                cy="46"
                r={RING_RADIUS}
                fill="none"
                stroke={stroke}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="60 205"
                style={{ transformOrigin: '46px 46px' }}
                className="motion-safe:animate-spin"
              />
            ) : (
              <circle
                cx="46"
                cy="46"
                r={RING_RADIUS}
                fill="none"
                stroke={stroke}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={RING_LENGTH}
                strokeDashoffset={RING_LENGTH * (1 - progress)}
                transform="rotate(-90 46 46)"
                className="transition-[stroke-dashoffset] duration-200 ease-linear motion-reduce:transition-none"
              />
            )}
          </svg>
          <button
            type="button"
            onClick={face?.onClick}
            disabled={!face}
            aria-label={face?.aria ?? (phase === 'saving' ? t('exam.speaking.saving') : t('exam.speaking.listen'))}
            className={cn(
              'absolute inset-[9px] flex items-center justify-center rounded-full transition',
              'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-[#0A1628]',
              'enabled:active:scale-[0.97] enabled:hover:brightness-110',
              red
                ? 'bg-[#EF4444] text-white shadow-[0_10px_22px_-10px_rgba(239,68,68,0.8)]'
                : phase === 'saving'
                  ? 'bg-[#E6F4EC] text-[#166534]'
                  : face
                    ? 'bg-[#0A1628] text-[#D1FE17] shadow-[0_10px_22px_-10px_rgba(10,22,40,0.7)]'
                    : 'bg-[#E6E9F2] text-[#8A93A8]',
            )}
          >
            {phase === 'recording' ? (
              <span className="h-[22px] w-[22px] rounded-md bg-white" aria-hidden />
            ) : phase === 'saving' ? (
              <Check className="h-6 w-6" strokeWidth={2.4} aria-hidden />
            ) : phase === 'failed' ? (
              <RotateCcw className="h-[26px] w-[26px]" aria-hidden />
            ) : phase === 'prep' ? (
              <Play className="h-6 w-6" fill="currentColor" aria-hidden />
            ) : (
              <Mic className="h-[26px] w-[26px]" aria-hidden />
            )}
          </button>
        </div>
      </div>
    </footer>
  );
}

function StatusLine({ phase, countdown, examinerListening }: { phase: Phase; countdown: number; examinerListening: boolean }) {
  const { t } = useTranslation();
  const recording = phase === 'recording';
  const failed = phase === 'failed';
  const text = failed
    ? t('exam.speaking.failed_status')
    : recording
      ? t('exam.speaking.recording')
      : phase === 'asking'
        ? t('exam.speaking.examiner_speaking')
        : phase === 'prep'
          ? t('exam.speaking.prep_status', { seconds: countdown })
          : phase === 'ready'
            ? t('exam.speaking.auto_start', { seconds: countdown })
            : phase === 'saving'
              ? t(examinerListening ? 'exam.speaking.examiner_thinking' : 'exam.speaking.saving_status')
              : '';
  if (!text) return null;
  return (
    <span className={cn('inline-flex items-center gap-2.5 text-[15px] font-bold', failed ? 'text-[#B42318]' : recording ? 'text-[#0A1628]' : 'text-[#0A1628]')}>
      <span
        className={cn(
          'h-2.5 w-2.5 shrink-0 rounded-full',
          failed
            ? 'bg-[#EF4444]'
            : recording
              ? 'bg-[#EF4444] motion-safe:animate-pulse'
              : phase === 'saving'
                ? 'bg-[#16A34A]'
                : 'bg-[#D1FE17] shadow-[0_0_0_2px_#0A1628] motion-safe:animate-pulse',
        )}
        aria-hidden
      />
      {text}
    </span>
  );
}

/** One symmetric run of bars, so the meter reads as a waveform. */
const BAR_SHAPE = [0.3, 0.55, 0.85, 0.6, 1, 0.4, 0.8, 0.65, 0.95, 0.35, 0.75, 0.5, 0.25, 0.45];
const BAR_SHAPE_LARGE = [...BAR_SHAPE, ...[...BAR_SHAPE].reverse()];

function LevelBars({ level, large = false }: { level: number; large?: boolean }) {
  const { t } = useTranslation();
  const pct = Math.round(level * 100);
  const shape = large ? BAR_SHAPE_LARGE : BAR_SHAPE;
  const max = large ? 52 : 32;
  return (
    <div
      className={cn('flex items-center', large ? 'h-[52px] gap-[3px]' : 'h-8 gap-1')}
      role="meter"
      aria-label={t('exam.speaking.voice_level')}
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {shape.map((s, i) => {
        const h = Math.max(6, Math.round(max * Math.min(1, level * 1.4) * s));
        return (
          <span
            key={i}
            className={cn('rounded-full transition-[height] duration-75', large ? 'w-1' : 'w-1.5', h > 6 ? 'bg-sky-500' : 'bg-slate-300')}
            style={{ height: h }}
          />
        );
      })}
    </div>
  );
}

export default SpeakingSectionView;
